import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Location, useLocation } from 'react-router-dom';

type PreviousPageContext =
  | {
      location: Location;
      fullString: string;
    }
  | undefined;

const ctx = createContext<PreviousPageContext>({} as PreviousPageContext);
const Provider = ctx.Provider;

/**
 * OA: A way to always keep track of the user's previous page. Main use for this is a graceful redirect after a login
 * or a similar operation. A component can access the previous page with the usePreviousPage hook below.
 *
 * NOTE: This saves the previous page only when the "pathname" changes, i.e. not when changing search/hash within one page.
 * However, it saves the complete location of the previous path, i.e. including search params and hash of the url.
 */
export const PreviousPageProvider = (props: { children: ReactNode }) => {
  const location = useLocation();

  const [locations, setLocations] = useState<{ currentPage: Location; previousPage?: Location }>({
    previousPage: undefined,
    currentPage: location,
  });

  useEffect(() => {
    setLocations(currentAndPreviousLocation => {
      const { previousPage, currentPage } = currentAndPreviousLocation;
      const currentPath = currentPage.pathname;
      const newPath = location.pathname;

      // If the user is browsing within the same page, we don't update the previousPage value,
      // but will update the possibly changed search/hash values to currentPage
      if (newPath === currentPath)
        return {
          previousPage,
          currentPage: location,
        };

      return {
        previousPage: currentPage,
        currentPage: location,
      };
    });
  }, [location]);

  // When landing to our UI, there's no previous location yet, but in order to make it easier to use this hook,
  // we just pass the current page so that there's no need to handle undefined's every time.
  const locationToUse = locations.previousPage;

  if (!locationToUse) {
    return <Provider value={undefined}>{props.children}</Provider>;
  }

  const { pathname, search, hash } = locationToUse;
  const fullString = `${pathname}${search}${hash}`;

  return <Provider value={{ location: locationToUse, fullString }}>{props.children}</Provider>;
};

/**
 * A hook to get the previous path the user navigated from.
 */
export function usePreviousPage() {
  return useContext(ctx);
}
